import React, { createContext, useContext, useState, useEffect, ReactNode, useCallback } from 'react';
import { complianceService } from '../services/api';

export interface Jurisdiction {
  id: string;
  name: string;
  state: string;
  abbreviation: string;
}

interface JurisdictionContextType {
  jurisdiction: Jurisdiction | null;
  jurisdictions: Jurisdiction[];
  setJurisdiction: (j: Jurisdiction) => void;
  isMultiJurisdiction: boolean;
  loading: boolean;
}

const JurisdictionContext = createContext<JurisdictionContextType | undefined>(undefined);

const STORAGE_KEY = 'selectedJurisdiction';

// Deterministic fallback for single-jurisdiction orgs (T-220-003) and for the
// period before getJurisdictions() resolves. The org's real jurisdiction list
// always comes from the backend via complianceService.getJurisdictions(); this
// default only guarantees that a single-jurisdiction org renders the static
// indicator instead of a broken/multi-org dropdown.
const DEFAULT_JURISDICTION: Jurisdiction = { id: 'tx', name: 'Texas', state: 'Texas', abbreviation: 'TX' };

export const JurisdictionProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [jurisdiction, setJurisdictionState] = useState<Jurisdiction | null>(null);
  const [jurisdictions, setJurisdictions] = useState<Jurisdiction[]>([DEFAULT_JURISDICTION]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;

    const applySaved = (list: Jurisdiction[]) => {
      try {
        const saved = localStorage.getItem(STORAGE_KEY);
        if (saved) {
          const parsed = JSON.parse(saved) as Jurisdiction;
          if (list.some((j) => j.id === parsed.id)) {
            setJurisdictionState(parsed);
            return;
          }
        }
      } catch {
        // ignore invalid storage
      }
      // No valid saved selection - default to the first (or only) jurisdiction so
      // the X-Jurisdiction header is always sent for single-jurisdiction orgs.
      const fallback = list[0] ?? null;
      setJurisdictionState(fallback);
      if (fallback) {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(fallback));
      }
    };

    (async () => {
      try {
        const res: any = await complianceService.getJurisdictions();
        const list: Jurisdiction[] =
          Array.isArray(res?.data) && res.data.length > 0 ? res.data : [DEFAULT_JURISDICTION];
        if (cancelled) return;
        setJurisdictions(list);
        applySaved(list);
      } catch {
        if (cancelled) return;
        // Lookup unavailable (offline/dev) - keep the deterministic default so a
        // single-jurisdiction org still shows the static indicator.
        setJurisdictions([DEFAULT_JURISDICTION]);
        applySaved([DEFAULT_JURISDICTION]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  const setJurisdiction = useCallback((j: Jurisdiction) => {
    setJurisdictionState(j);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(j));
  }, []);

  const isMultiJurisdiction = jurisdictions.length > 1;

  return (
    <JurisdictionContext.Provider
      value={{
        jurisdiction,
        jurisdictions,
        setJurisdiction,
        isMultiJurisdiction,
        loading,
      }}
    >
      {children}
    </JurisdictionContext.Provider>
  );
};

export const useJurisdiction = (): JurisdictionContextType => {
  const context = useContext(JurisdictionContext);
  if (!context) {
    throw new Error('useJurisdiction must be used within JurisdictionProvider');
  }
  return context;
};